import { Link, router } from '@inertiajs/react'
import { useState } from 'react'

interface ProjectInfo {
  id: number
  name: string
  health: string
  progress_percent: number
  latest_status_update_summary: string | null
  latest_status_update_at: string | null
  owner_full_name: string
  owner_id?: number
}

interface Props {
  projects: ProjectInfo[]
  currentUserId?: number
  currentUserIsAdmin?: boolean
}

const HEALTH_COLORS: Record<string, string> = {
  on_track: 'bg-green-100 text-green-800',
  at_risk: 'bg-amber-100 text-amber-800',
  off_track: 'bg-red-100 text-red-800'
}

function relativeTime(iso: string | null) {
  if (!iso) return 'no update yet'
  const diff = Date.now() - new Date(iso).getTime()
  const h = Math.floor(diff / 3600_000)
  if (h < 1) return 'just now'
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function ProjectRow({ p, canPost }: { p: ProjectInfo; canPost: boolean }) {
  const [open, setOpen] = useState(false)
  const [health, setHealth] = useState(p.health)
  const [percent, setPercent] = useState(p.progress_percent)
  const [summary, setSummary] = useState('')
  const [busy, setBusy] = useState(false)

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    router.post(`/projects/${p.id}/status_updates`,
      { status_update: { health, progress_percent: percent, summary } },
      { preserveScroll: true, onFinish: () => { setBusy(false); setOpen(false); setSummary('') } }
    )
  }

  return (
    <li className="bg-white border rounded-lg p-3">
      <div className="flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Link href={`/projects/${p.id}`} className="font-semibold text-text-primary hover:underline">{p.name}</Link>
            <span className={`text-xs px-2 py-0.5 rounded ${HEALTH_COLORS[p.health] || ''}`}>{p.health.replace('_', ' ')}</span>
          </div>
          <div className="text-xs text-text-muted mt-0.5">
            {p.owner_full_name} · last update {relativeTime(p.latest_status_update_at)}
          </div>
          {p.latest_status_update_summary && (
            <p className="text-sm text-text-primary mt-1 line-clamp-2">{p.latest_status_update_summary}</p>
          )}
        </div>
        <div className="w-40">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-gray-200 rounded overflow-hidden">
              <div className="h-full bg-[#15532E]" style={{ width: `${p.progress_percent}%` }} />
            </div>
            <span className="text-xs text-text-secondary w-10 text-right">{p.progress_percent}%</span>
          </div>
          {canPost && (
            <button type="button" onClick={() => setOpen(!open)} className="text-xs text-[#ED1C24] mt-2 hover:underline">
              {open ? 'Cancel' : 'Post status update'}
            </button>
          )}
        </div>
      </div>
      {open && canPost && (
        <form onSubmit={submit} className="mt-3 border-t pt-3 grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
          <div>
            <label className="block text-xs text-text-muted mb-1">Health</label>
            <select value={health} onChange={(e) => setHealth(e.target.value)} className="w-full border rounded px-2 py-1 text-sm">
              <option value="on_track">On track</option>
              <option value="at_risk">At risk</option>
              <option value="off_track">Off track</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1">% complete</label>
            <input type="number" min={0} max={100} value={percent} onChange={(e) => setPercent(Number(e.target.value))} className="w-full border rounded px-2 py-1 text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-text-muted mb-1">Summary</label>
            <input type="text" value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="What changed since last meeting?" className="w-full border rounded px-2 py-1 text-sm" />
          </div>
          <div className="md:col-span-4 flex justify-end">
            <button type="submit" disabled={busy} className="px-3 py-1 text-xs text-white rounded" style={{ backgroundColor: '#ED1C24' }}>
              {busy ? 'Posting…' : 'Post update'}
            </button>
          </div>
        </form>
      )}
    </li>
  )
}

export default function SectionProjects({ projects, currentUserId, currentUserIsAdmin }: Props) {
  if (!projects || projects.length === 0) {
    return <p className="text-text-muted">No active projects linked to this team yet.</p>
  }
  return (
    <div className="space-y-3">
      <p className="text-sm text-text-muted">Review status on this team's active projects. Owners can post a quick status update inline.</p>
      <ul className="space-y-2">
        {projects.map((p) => (
          <ProjectRow
            key={p.id}
            p={p}
            canPost={!!(currentUserIsAdmin || (currentUserId && p.owner_id === currentUserId))}
          />
        ))}
      </ul>
    </div>
  )
}
